import React, { useState, useEffect } from 'react';
import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import AdminPanel from './components/AdminPanel';
import UserPanel from './components/UserPanel';
import LoginForm from './components/LoginForm';
import ApplicationPeriodsSection from './components/ApplicationPeriodsSection';
import { ToastProvider } from './context/ToastContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import './App.css';
import './components/AboutSection.css';
import { Modal, TermsContent, PrivacyContent } from './components/LegalModals';

function AppContent() {
  const { t } = useLanguage();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userType, setUserType] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showAllFaqs, setShowAllFaqs] = useState(false);

  // Modal states
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  // State for expanded blog posts
  const [expandedBlogs, setExpandedBlogs] = useState({});

  useEffect(() => {
    // Check if user is already authenticated
    const adminToken = localStorage.getItem('adminToken');
    const adminUser = localStorage.getItem('adminUser');
    const userToken = localStorage.getItem('userToken');
    const userData = localStorage.getItem('userData');

    if (adminToken && adminUser) {
      setIsAuthenticated(true);
      setUserType('admin');
    } else if (userToken && userData) {
      setIsAuthenticated(true);
      setUserType('user');
    }
    setIsLoading(false);
  }, []);

  // Check routes
  const isAdminRoute = window.location.pathname === '/admin';
  const isUserRoute = window.location.pathname === '/user';

  // Toggle blog expansion
  const toggleBlogExpand = (blogId) => {
    setExpandedBlogs(prev => ({
      ...prev,
      [blogId]: !prev[blogId]
    }));
  };

  // FAQ data
  const faqs = [
    {
      question: "1. What is Vikas Fund?",
      answer: "Vikas Fund is a platform that reviews and evaluates ideas, projects, business concepts, and social initiatives submitted by applicants for possible financial support or funding assistance."
    },
    {
      question: "2. Who can apply for Vikas Fund?",
      answer: "Any eligible individual, startup, group, organization, or applicant with a lawful and genuine idea or project may apply, subject to the Terms & Conditions."
    },
    {
      question: "3. Is there any age limit for applicants?",
      answer: "Applicants must be at least 18 years old. Applicants below 18 years of age must provide parental or guardian consent."
    },
    {
      question: "4. How can I submit my application?",
      answer: "Applications can only be submitted through the official online submission form available on the Vikas Fund website."
    },
    {
      question: "5. Is the processing fee refundable?",
      answer: "No. The processing fee is strictly non-refundable under any circumstances."
    },
    {
      question: "6. Does payment of the fee guarantee funding?",
      answer: "No. Payment of the processing fee or submission of an application does not guarantee approval, funding, or selection."
    },
    {
      question: "7. How is the funding amount decided?",
      answer: "The funding amount is determined solely by Vikas Fund based on evaluation, project requirements, verification, and resource availability."
    },
    {
      question: "8. Can applicants request a specific funding amount?",
      answer: "No. Applicants cannot demand or insist upon any specific funding amount."
    },
    {
      question: "9. How long does the evaluation process take?",
      answer: "Evaluation timelines are tentative and may vary depending on the number of applications, verification process, and operational requirements."
    },
    {
      question: "10. Will I receive updates about my application?",
      answer: "Applicants may receive updates or status notifications through official email communication."
    },
    {
      question: "11. What happens after provisional funding approval?",
      answer: "Applicants may be required to submit additional proofs, quotations, utilization plans, bank details, or verification documents before final funding release."
    },
    {
      question: "12. What if I fail to submit documents within the required timeline?",
      answer: "Failure to submit required documents within the prescribed timeline may result in cancellation or rejection of the proposed funding."
    },
    {
      question: "13. Who owns the submitted idea?",
      answer: "Ownership rights of the submitted idea remain with the applicant."
    },
    {
      question: "14. Can Vikas Fund reject an application without giving reasons?",
      answer: "Yes. Vikas Fund reserves the right to reject applications without disclosing detailed reasons."
    },
    {
      question: "15. How can I contact Vikas Fund?",
      answer: "Applicants may contact Vikas Fund through the official email address mentioned on the website."
    },
    {
      question: "16. Does Vikas Fund guarantee project success?",
      answer: "No. Vikas Fund does not guarantee project success, future profits, business performance, or future support."
    },
    {
      question: "17. Can duplicate applications be submitted?",
      answer: "Duplicate submissions may not be considered valid, and processing fees for duplicate submissions are non-refundable."
    },
    {
      question: "18. Is my information secure?",
      answer: "Vikas Fund implements reasonable security measures; however, complete security of internet-based systems cannot be guaranteed."
    },
    {
      question: "19. Can Terms & Conditions or Privacy Policy change?",
      answer: "Yes. Vikas Fund reserves the right to modify or update policies, terms, or procedures at any time."
    },
    {
      question: "20. Where can I read the official Terms & Conditions and Privacy Policy?",
      answer: "The latest official Terms & Conditions and Privacy Policy are available on the Vikas Fund website."
    }
  ];

  // Blog data with full content
  const blogs = [
    {
      id: 1,
      title: "Ideas Have the Power to Change Everything",
      preview: "Every successful journey begins with a simple idea. A thought. A vision. A dream that refuses to stay unnoticed. At Vikas Fund, we believe that innovation is not limited to big cities, large companies, or powerful institutions.",
      fullContent: `Ideas Have the Power to Change Everything Every successful journey begins with a simple idea.
A thought. A vision. A dream that refuses to stay unnoticed.
At Vikas Fund, we believe that innovation is not limited to big cities, large companies, or powerful institutions. Some of the most meaningful ideas come from students, young creators, rural innovators, artists, social workers, startups, and ordinary individuals who see problems differently and imagine better solutions.
Many talented people never receive an opportunity not because their ideas lack value, but because they lack visibility, guidance, resources, or support.
That is where Vikas Fund aims to create impact.`
    },
    {
      id: 2,
      title: "A Platform Built for Possibilities",
      preview: "Vikas Fund was created with a simple purpose to provide a platform where people can present their ideas, projects, and initiatives for professional evaluation and possible financial support.",
      fullContent: `A Platform Built for Possibilities
Vikas Fund was created with a simple purpose to provide a platform where people can present their ideas, projects, and initiatives for professional evaluation and possible financial support.
We understand that behind every idea there is effort, passion, time, and belief. Whether it is a startup concept, a social initiative, a creative project, or a community-focused vision, every submission represents someone's ambition to build something meaningful.
Our platform encourages individuals to step forward confidently and share ideas that have the potential to create positive change.`

    },
    {
      id: 3,
      title: "Encouraging Creativity & Innovation",
      preview: "Innovation does not always start in boardrooms. Sometimes it begins in classrooms, villages, small studios, workshops, or within people who simply refuse to give up on their vision.",
      fullContent: `Encouraging Creativity & Innovation
Innovation does not always start in boardrooms.
Sometimes it begins in classrooms, villages, small studios, workshops, or within people who simply refuse to give up on their vision.
We support a culture that values:

• Creative Thinking  
• Problem Solving  
• Social Impact  
• Entrepreneurship  
• Community Development  
• Art & Storytelling  
• Innovation with Purpose

Every generation brings new ideas capable of transforming society. By encouraging innovation and independent thinking, we move closer toward meaningful progress.`
    },

    {
      id: 4,
      title: "Why Ideas Matter",
      preview: "A single idea can solve real problems, improve lives, create opportunities, and inspire communities toward meaningful progress.",
      fullContent: `Why Ideas Matter

A single idea can:

• Solve a real problem  
• Improve lives  
• Create employment  
• Inspire communities  
• Build businesses  
• Support culture and creativity  
• Bring long-term positive impact

History has repeatedly shown that great changes often begin with small but determined ideas.`
    },


    {
      id: 5,
      title: "Building Opportunities Through Responsible Evaluation",
      preview: "At Vikas Fund, submissions are reviewed through a structured evaluation process focused on originality, feasibility, clarity, purpose, and potential impact.",
      fullContent: `Building Opportunities Through Responsible Evaluation
At Vikas Fund, submissions are reviewed through a structured evaluation process. We focus on originality, feasibility, clarity, purpose, and potential impact while reviewing applications.
Our aim is not only to encourage ideas but also to create a responsible and transparent platform for applicants who wish to present their vision professionally.`
    },


    {
      id: 6,
      title: "Looking Toward the Future",
      preview: "India is filled with talented individuals, ambitious creators, and innovative thinkers waiting for an opportunity to be seen.",
      fullContent: `Looking Toward the Future
India is filled with talented individuals, ambitious creators, and innovative thinkers waiting for an opportunity to be seen.
Vikas Fund hopes to become a bridge between ideas and possibilities a platform that motivates people to think bigger, create fearlessly, and work toward meaningful impact.
Because every meaningful change begins when someone decides to believe in an idea.`
    }
  ];

  const visibleFaqs = showAllFaqs ? faqs : faqs.slice(0, 3);


  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <div style={{ color: 'white', fontSize: '1.2rem' }}>Loading...</div>
      </div>
    );
  }

  if (isAdminRoute) {
    if (isAuthenticated && userType === 'admin') {
      return (
        <LanguageProvider>
          <ToastProvider>
            <AdminPanel />
          </ToastProvider>
        </LanguageProvider>
      );
    } else {
      return (
        <LanguageProvider>
          <ToastProvider>
            <LoginForm />
          </ToastProvider>
        </LanguageProvider>
      );
    }
  }

  if (isUserRoute) {
    if (isAuthenticated && userType === 'user') {
      return (
        <LanguageProvider>
          <ToastProvider>
            <UserPanel />
          </ToastProvider>
        </LanguageProvider>
      );
    } else {
      return (
        <LanguageProvider>
          <ToastProvider>
            <LoginForm />
          </ToastProvider>
        </LanguageProvider>
      );
    }
  }

  return (
    <div className="App">
      <Navbar />
      <HeroSection />

      {/* Funding Options Section */}
      <section id="funding" className="section">
        <div className="container">
          <h2 className="section-title">We Are Support</h2>
          <div className="funding-grid">
            <div className="funding-card">
              <img
                className="support-logo"
                src="/Ideas.png"
                alt="Innovation & Ideas"
              />
              <h3>Innovation & Ideas</h3>
              <p>Empowering creative minds, students, and entrepreneurs to turn innovative ideas into reality.</p>
            </div>
            <div className="funding-card">
              <img
                className="support-logo"
                src="/Creative.png"
                alt="Vikas Fund"
              />
              <h3>Creative & Cultural Initiatives</h3>
              <p>Supporting film, art, storytelling, and social creativity that inspires positive change.</p>
            </div>
            <div className="funding-card">
              <img
                className="support-logo"
                src="/Community.png"
                alt="Vikas Fund"
              />
              <h3>Community & Development</h3>
              <p>Encouraging community-driven growth, impactful initiatives, and meaningful social progress.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works Section */}
      <section id="how-it-works" className="section">
        <div className="container">
          <h2 className="section-title">How it Works</h2>
          <div className="steps-container">
            <div className="step">
              <div className="step-number">1</div>
              <h3>Apply Online</h3>
              <p>Fill out and submit your application through our online platform.</p>
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <h3>Application Review</h3>
              <p>Your application will be reviewed and evaluated by our team.</p>
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <h3>Funding Decision</h3>
              <p>Selected applicants will be informed regarding funding support through official communication.</p>
            </div>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="section about-section">
        <div className="about-hero-banner">
          <div className="about-hero-overlay">
            <div className="about-hero-text">
                <h2 className="section-title">About Us</h2>
              <p className="about-hero-tagline">Empowering Ideas. Supporting Possibilities.</p>
            </div>
          </div>
        </div>

        <div className="container">
          {/* Intro Block */}
          <div className="about-intro-block">
            <p>Vikas Fund is an independent initiative dedicated to identifying, reviewing, and supporting innovative ideas, meaningful projects, business concepts, and social initiatives from individuals, startups, creators, and communities across India.</p>
            <p>We believe that powerful ideas can come from anywhere from students, entrepreneurs, artists, rural innovators, social workers, travelers, and passionate individuals who simply need the right opportunity and support to move forward.</p>
            <p>Through our structured application and evaluation process, Vikas Fund provides a platform where applicants can present their ideas for possible financial assistance and development support.</p>
            <p>Our objective is to encourage originality, practical thinking, creativity, and positive impact by giving deserving ideas a chance to be evaluated professionally.</p>
          </div>

          {/* What We Do */}
          <div className="about-card-section">
            <div className="about-card-header">
              <span className="about-card-icon">🚀</span>
              <h3>What We Do</h3>
            </div>
            <p className="about-card-desc">Vikas Fund accepts applications through its official online platform, where applicants can submit:</p>
            <div className="about-pills-grid">
              {['Business Ideas','Startup Concepts','Creative Projects','Social Initiatives','Community Development Plans','Innovative Solutions','Individual or Group-Based Projects'].map((item, i) => (
                <span key={i} className="about-pill">{item}</span>
              ))}
            </div>
            <p className="about-card-desc" style={{marginTop:'1.2rem'}}>Each application may include supporting documents, project plans, estimated budgets, and video presentations to help our team understand the vision and potential impact of the proposal.</p>
          </div>

          {/* Evaluation + Vision + Transparency */}
          <div className="about-three-grid">
            <div className="about-feature-card">
              <div className="about-feature-icon">🔍</div>
              <h4>Our Evaluation Process</h4>
              <p>Every submission undergoes an internal review based on multiple factors:</p>
              <ul className="about-feature-list">
                <li>Originality of the idea</li>
                <li>Feasibility &amp; implementation</li>
                <li>Social or commercial impact</li>
                <li>Resource requirements</li>
                <li>Documentation &amp; verification</li>
                <li>Presentation and clarity</li>
              </ul>
              <p className="about-feature-note">Funding decisions are made independently at the sole discretion of the Vikas Fund team.</p>
            </div>

            <div className="about-feature-card about-feature-card--accent">
              <div className="about-feature-icon">🌟</div>
              <h4>Our Vision</h4>
              <p>Our vision is to create a platform that encourages people to bring forward meaningful ideas without limitations of background, location, or profession.</p>
              <p style={{marginTop:'1rem'}}>We aim to build an ecosystem where innovative thinking, practical solutions, and creative efforts receive visibility and an opportunity for support.</p>
            </div>

            <div className="about-feature-card">
              <div className="about-feature-icon">🤝</div>
              <h4>Transparency &amp; Responsibility</h4>
              <p>Vikas Fund operates with a focus on structured evaluation, responsible communication, and transparent application procedures.</p>
              <p style={{marginTop:'1rem'}}>Applicants are encouraged to carefully review our Terms &amp; Conditions and Privacy Policy before submitting any application.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Application Periods & Results Section */}
      <ApplicationPeriodsSection />

      {/* Blog Section */}
      <section id="blog" className="section">
        <div className="container">
          <h2 className="section-title">Latest from Our Blog</h2>
          <div className="blog-grid">
            {blogs.map((blog) => (
              <article className="blog-card" key={blog.id}>
                <div className="blog-image"></div>
                <div className="blog-content">
                  <h3>{blog.title}</h3>
                  <p>
                    {expandedBlogs[blog.id]
                      ? blog.fullContent
                      : blog.preview}
                  </p>
                  {!expandedBlogs[blog.id] && blog.fullContent.length > blog.preview.length && (
                    <button
                      onClick={() => toggleBlogExpand(blog.id)}
                      className="read-more-btn"
                      style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        border: 'none',
                        padding: '8px 20px',
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        borderRadius: '20px',
                        cursor: 'pointer',
                        marginTop: '1rem',
                        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 2px 10px rgba(0, 0, 0, 0.1)';
                      }}
                    >
                      Read More
                    </button>
                  )}
                  {expandedBlogs[blog.id] && (
                    <button
                      onClick={() => toggleBlogExpand(blog.id)}
                      className="show-less-btn"
                      style={{
                        background: 'transparent',
                        color: '#667eea',
                        border: '2px solid #667eea',
                        padding: '8px 20px',
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        borderRadius: '20px',
                        cursor: 'pointer',
                        marginTop: '1rem',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#667eea';
                        e.target.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'transparent';
                        e.target.style.color = '#667eea';
                      }}
                    >
                      Show Less
                    </button>
                  )}
                  <div className="blog-date" style={{ marginTop: '1rem' }}>
                    {blog.date}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="section">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-container">
            {visibleFaqs.map((faq, index) => (
              <div className="faq-item" key={index}>
                <h3>{faq.question}</h3>
                <p>{faq.answer}</p>
              </div>
            ))}
          </div>
          {!showAllFaqs && faqs.length > 3 && (
            <div style={{ textAlign: 'center', marginTop: '2rem' }}>
              <button
                onClick={() => setShowAllFaqs(true)}
                className="show-more-btn"
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '12px 30px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  borderRadius: '30px',
                  cursor: 'pointer',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.1)';
                }}
              >
                Show More FAQs ({faqs.length - 3} more)
              </button>
            </div>
          )}
          {showAllFaqs && faqs.length > 3 && (
            <div style={{ textAlign: 'center', marginTop: '2rem' }}>
              <button
                onClick={() => setShowAllFaqs(false)}
                className="show-less-btn"
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '12px 30px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  borderRadius: '30px',
                  cursor: 'pointer',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.1)';
                }}
              >
                Show Less
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <img
                className="footer-logo"
                src="/bottomlogo2.ico"
                alt="Vikas Fund"
              />
            </div>
            <div className="footer-section">
              <h4>Quick Links</h4>
              <ul>
                <li><a href="#funding">We Are Support</a></li>
                <li><a href="#how-it-works">How it Works</a></li>
                <li><a href="#about">About Us</a></li>
                <li><a href="#blog">Blog</a></li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>Contact</h4>
              <p>Email: info@vikasfund.com</p>
              <p>To ensure your communication is safe, fast, and smooth, please use the email below to contact us.</p>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2026 Vikas Fund. All rights reserved.</p>
            <div className="footer-legal-buttons">
              <button
                className="legal-btn"
                onClick={() => setShowTermsModal(true)}
              >
                Terms & Conditions
              </button>
              <button
                className="legal-btn"
                onClick={() => setShowPrivacyModal(true)}
              >
                Privacy Policy
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Terms & Conditions Modal */}
      <Modal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        title="Vikasfund: Terms & Conditions (English Version)"
      >
        <TermsContent />
      </Modal>

      {/* Privacy Policy Modal */}
      <Modal
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
        title="Vikasfund: Privacy Policy"
      >
        <PrivacyContent />
      </Modal>
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <ToastProvider>
        <AppContent />
      </ToastProvider>
    </LanguageProvider>
  );
}

export default App;

















